import React from 'react'
import cx from 'classnames'
import { Icon, IconWeight, Plus } from '@phosphor-icons/react'
import { Button as ShadcnButton } from '@alphabait/ui'
import './button.css'
import { useTranslation } from 'react-i18next'

type ButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> & {
  primary?: boolean
  size?: 'small-icon' | 'small' | 'normal' | 'big' | 'smallest'
  label: string
  iconWeight?: IconWeight
  IconLeft?: Icon
  IconRight?: Icon
  isIncentive?: boolean
  onClick?: () => void
  testId?: string
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ ...props }, ref) => {
    const { t } = useTranslation()
    const {
      className,
      primary = false,
      IconLeft,
      IconRight,
      label,
      size = 'normal',
      type = 'button',
      iconWeight = 'regular',
      isIncentive = false,
      testId,
      ...rest
    } = props
    const iconSize = size === 'small-icon' ? 16 : 24
    const mode = primary
      ? 'bg-primary text-white hover:bg-marine-300 focus:bg-primary focus-visible:outline focus:ring-primary'
      : 'bg-white border-primary text-primary hover:border-primary hover:text-white focus:text-black-100 focus:border-black-100 focus:ring-black-100 '
    return (
      <ShadcnButton
        ref={ref}
        type={type}
        className={cx(
          'flex flex-row items-center justify-center text-inherit',
          'rounded-md cursor-pointer border focus:ring-2 focus:ring-offset-2',
          `button--${size}`,
          mode,
          className,
        )}
        data-testid={testId}
        {...rest}
      >
        {isIncentive ? (
          <div className="space-y-2">
            <Plus size={35} color="#3CDC4B" className="m-auto" />
            <p className="text-sm font-bold h-inherit">
              {t('add_incentive.add_incentive')}
            </p>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2 m-auto">
            {IconLeft && (
              <IconLeft {...{ weight: iconWeight, size: iconSize }} />
            )}
            <span
              className={cx(
                'whitespace-nowrap',
                className?.includes('text-')
                  ? className
                    .split(' ')
                    .filter((c) => c.startsWith('text-'))
                    .join(' ')
                  : '',
              )}
            >
              {label}
            </span>
            {IconRight && (
              <IconRight {...{ weight: iconWeight, size: iconSize }} />
            )}
          </div>
        )}
      </ShadcnButton>
    )
  },
)

Button.displayName = 'Button'
